import React from 'react';
import { T, NUMERIC } from '../customer/customers.theme';
import { CalendarIcon, ChevronIcon } from '../customer/CustomersUI';
import { COPY } from './expenses.copy';
import { money, percent } from './expenses.summary';

/**
 * The headline, and where the money went.
 *
 * The phone stacks these: a tinted header carrying the date control and the
 * total, then the category cards, then the transactions. A desktop has the
 * width to put the breakdown beside the list instead of above it, so the
 * figure and the categories become one column on the left and the list gets
 * the rest — the same three pieces, read in the same order, without scrolling
 * past the summary to reach a transaction.
 */

export function ExpenseHeadline({ total, count, rangeLabel, onPickDate, anchorRef, children }) {
  return (
    <div
      style={{
        position: 'relative',
        // The date popover hangs off the bottom of this card and overlaps the
        // transactions beside it. The rail is painted after the list, so this
        // only has to hold against anything positioned later.
        zIndex: 5,
        flexShrink: 0,
        padding: '18px 20px 20px',
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusPanel,
      }}
    >
      <button
        ref={anchorRef}
        type="button"
        onClick={onPickDate}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          height: 40,
          padding: '0 14px',
          background: T.sunk,
          border: `1px solid ${T.border}`,
          borderRadius: T.radiusControl,
          font: 'inherit',
          fontSize: 12.5,
          color: T.ink,
          cursor: 'pointer',
          boxSizing: 'border-box',
        }}
      >
        <span style={{ display: 'flex', color: T.inkFaint }}>
          <CalendarIcon size={15} />
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: 'left',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {rangeLabel}
        </span>
        <span style={{ display: 'flex', color: T.inkFaint }}>
          <ChevronIcon size={15} />
        </span>
      </button>

      {/* The popover anchors to this card, so it is rendered by the caller
          inside it rather than floating over the page. */}
      {children}

      <div style={{ paddingTop: 18 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: T.inkFaint,
            letterSpacing: 0.3,
          }}
        >
          {COPY.totalExpense}
        </span>
        <div
          style={{
            paddingTop: 4,
            fontSize: 32,
            fontWeight: 800,
            letterSpacing: -1,
            color: T.ink,
            ...NUMERIC,
          }}
        >
          {money(total)}
        </div>
        <div style={{ paddingTop: 4, fontSize: 12, color: T.inkFaint }}>
          {COPY.entries(count)}
        </div>
      </div>
    </div>
  );
}

/**
 * Where it went, and a way into it.
 *
 * The phone lists a name and an amount per category. The share is added here
 * because a desktop has room for it, and because the question a manager
 * actually asks of this list is which line is the big one — a bar answers that
 * faster than reading five numbers.
 *
 * Each line is also a filter. Seeing that rent is 70% of the month leads
 * straight to "which payments were those", and on a desk the answer can appear
 * beside the question rather than on another screen. Clicking the active line
 * again clears it, so the control is its own way out.
 *
 * The lines themselves are NOT narrowed by that selection — they are worked
 * out before it is applied. A breakdown that collapsed to the one category you
 * just picked would leave nothing to click next.
 */
export function ExpenseCategories({ categories, activeName, onSelect }) {
  if (!categories.length) return null;

  return (
    <div
      style={{
        // Takes whatever height is left under the headline and scrolls inside
        // itself. The column around it must NOT scroll, or it would clip the
        // date popover that hangs off the card above.
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 20px 18px',
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusPanel,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: T.ink }}>
          {COPY.categories}
        </span>
        {activeName ? (
          <button
            type="button"
            onClick={() => onSelect(null)}
            style={{
              padding: 0,
              background: 'transparent',
              border: 'none',
              font: 'inherit',
              fontSize: 11.5,
              fontWeight: 600,
              color: T.brandInk,
              cursor: 'pointer',
            }}
          >
            {COPY.showAll}
          </button>
        ) : null}
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          paddingTop: 10,
          // Room for the row padding, so the hover tint is not clipped against
          // the card edge.
          margin: '0 -8px',
          padding: '10px 8px 0',
        }}
      >
        {categories.map((category) => (
          <CategoryLine
            key={category.name}
            category={category}
            active={activeName === category.name}
            dimmed={Boolean(activeName) && activeName !== category.name}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * One category, as a button.
 *
 * Selected is the brand tint and a heavier bar; the unselected lines fade
 * rather than disappear, so the shape of the month stays readable while one
 * part of it is being looked at.
 */
function CategoryLine({ category, active, dimmed, onSelect }) {
  const [hovered, setHovered] = React.useState(false);

  return (
    <button
      type="button"
      aria-pressed={active}
      title={category.name}
      // The active line clears the filter, so the control is its own way out.
      onClick={() => onSelect(active ? null : category.name)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        width: '100%',
        padding: '9px 10px',
        background: active ? T.selectedBg : hovered ? T.hover : 'transparent',
        border: `1px solid ${active ? T.selectedBorder : 'transparent'}`,
        borderRadius: T.radiusControl,
        font: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        opacity: dimmed && !hovered ? 0.55 : 1,
        boxSizing: 'border-box',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 10, width: '100%' }}>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 13,
            fontWeight: active ? 600 : 400,
            color: active ? T.brandInk : T.ink,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {category.name}
        </span>
        <span style={{ fontSize: 11.5, color: T.inkFaint, ...NUMERIC }}>
          {percent(category.share)}
        </span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: active ? T.brandInk : T.ink,
            ...NUMERIC,
          }}
        >
          {money(category.amount)}
        </span>
      </span>

      <span
        style={{
          display: 'block',
          width: '100%',
          height: 4,
          background: T.hairline,
          borderRadius: T.radiusPill,
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            display: 'block',
            // A hair of width on a rounding-to-zero share, so a category that
            // exists is never an empty track.
            width: `${Math.max(2, Math.min(100, category.share))}%`,
            height: '100%',
            background: active ? T.brandInk : T.brand,
            borderRadius: T.radiusPill,
          }}
        />
      </span>
    </button>
  );
}
